import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { useAuthStore } from '../store/auth';

/** Navbar "active work" counts. Keys are present only for permissions the user holds. */
export interface BadgeCounts {
  /** The approvals hub total — every decision waiting on this user, across every workflow. */
  approvalsWaiting?: number;
  /** Work this user RAISED that was sent back to them to fix. Counted for every role, not just approvers. */
  approvalsMine?: number;
  ordersActive?: number;
  ordersPendingApproval?: number;
  ordersAwaitingTransfer?: number;
  adjustmentsPendingApproval?: number;
  transfersAwaitingReceipt?: number;
  transfersAwaitingDispatch?: number;
  /**
   * Transfers received short or damaged whose difference nobody has settled.
   *
   * The third kind of outstanding transfer work, and for a long time the only one no counter
   * carried — the register offered a Resolve button that no number anywhere pointed at.
   */
  transfersAwaitingResolution?: number;
  /**
   * Days the OLDEST outstanding transfer has been waiting ON ITS CURRENT STEP.
   *
   * A count says how MUCH is waiting and nothing about how long. Ten transfers raised this morning
   * and one in transit since July both read as a number in a circle.
   */
  transfersOldestWaitingDays?: number;
  paymentsPending?: number;
  expenseAwaitingMe?: number;
  advanceRequestsAwaitingMe?: number;
  myAdvanceRequestsPending?: number;
  openInvoices?: number;
  /**
   * Tally invoices still to upload for packed goods — one per waiting shipment on a per-shipment order, one for a
   * whole-order invoice. Present only for INVOICE.CREATE holders; the orders register's "To invoice" card is these orders.
   */
  invoicesToUpload?: number;
  openComplaints?: number;
  attendanceAnomalies?: number;
  attendanceIncomplete?: number;
  /**
   * The oldest day still waiting on an attendance reviewer — `YYYY-MM-DD`, absent when nothing is.
   *
   * The two counts above run to the beginning of time; the attendance register opens on the last
   * seven days. This date is what lets the tab badge open the whole backlog it counted instead of
   * dropping the reviewer on a week that holds one of the eight.
   */
  attendanceReviewSince?: string;
  regularizationsPending?: number;
  territoriesUnassigned?: number;
  coverageNeeded?: number;
  upcomingEvents?: number;
  openInquiries?: number;
  shortfallsOpen?: number;
  returnsActionable?: number;
}

/**
 * Live badge counts for the navbar. Polls every 60s and refetches on tab focus,
 * so pending-work indicators stay current without a reload. Backend computes them
 * permission-gated + self-scoped; the UI only renders.
 */
export function useBadges(): BadgeCounts {
  const token = useAuthStore((s) => s.accessToken);
  const { data } = useQuery({
    queryKey: ['badges'],
    queryFn: () => request<BadgeCounts>({ url: '/badges' }),
    enabled: !!token,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  return data ?? {};
}
